/**
 * Sol panel icerik yapilandirmasi — FW-126 KAPSAM B ("veri-gudumlu sol
 * panel"). design/current/Auth v2.dc.html satir ~401-411: sol panel
 * `AuthPageContent[flowKey].blocks[]` okur (blok tipleri: image / headline /
 * modules / checklist / quote) + `proof[]`. Bugun bu, Console'un "Karsilama
 * ekranlari" modulunden yonetilecek; o modul henuz yok, bu yuzden BUGUN bir
 * seed olarak burada tanimli (tasarimin kendi SEED.giris sabiti ile birebir).
 *
 * KURAL (brief): "SABLONDA SABIT METIN YASAK" — bu dosya METIN degil,
 * i18n ANAHTARI tasir. Gercek Turkce/Ingilizce cumleler yalniz
 * locales/tr.json + locales/en.json icinde yasar; App.vue
 * bu anahtarlari t() ile cozer ve ortak AuthWelcomePanel'e aktarir. Seed cumlesini .vue sablonlarinda arayan
 * bir grep bu yuzden hicbir zaman eslesmez (kabul kriteri 2).
 */

import type { MessageKey } from '@fusapp-work/shell'
import type { WelcomeContent, WelcomeModuleItem } from '@fusapp-work/ui-kit'

const MODULE_ITEMS: readonly WelcomeModuleItem<MessageKey>[] = [
  { nameKey: 'welcome.signIn.modules.crm.name', lineKey: 'welcome.signIn.modules.crm.line' },
  { nameKey: 'welcome.signIn.modules.invoice.name', lineKey: 'welcome.signIn.modules.invoice.line' },
  { nameKey: 'welcome.signIn.modules.expense.name', lineKey: 'welcome.signIn.modules.expense.line' },
  { nameKey: 'welcome.signIn.modules.saas.name', lineKey: 'welcome.signIn.modules.saas.line' },
  { nameKey: 'welcome.signIn.modules.support.name', lineKey: 'welcome.signIn.modules.support.line' },
  { nameKey: 'welcome.signIn.modules.group.name', lineKey: 'welcome.signIn.modules.group.line' },
]

/** Giris akisi (id.fusapp.com) icin seed — design/current/Auth v2.dc.html SEED.giris. */
export const authWelcomeContent: WelcomeContent<MessageKey> = {
  blocks: [
    { type: 'image', altKey: 'welcome.signIn.imageAlt', specKey: 'welcome.signIn.imageSpec' },
    {
      type: 'headline',
      kickerKey: 'welcome.signIn.kicker',
      titleKey: 'welcome.signIn.title',
      subKey: 'welcome.signIn.sub',
    },
    { type: 'modules', items: MODULE_ITEMS },
  ],
  proofKeys: [
    'welcome.proof.countries',
    'welcome.proof.currencies',
    'welcome.proof.compliance',
    'welcome.proof.dataResidency',
  ],
}

/**
 * Davet akisi (14-davet / 15-davet-yeni-hesap / 16-davet-mevcut) icin seed —
 * FW-175 (main'in bulgusu: onayli `auth__14/15/16.png`'nin SOLU bu icerik
 * hic KURULMADIGI icin YANLISLIKLA giris seed'ini basiyordu).
 *
 * METIN KAYNAGI: durumlar.json'daki 14/15/16 kayitlarinin `ipucu`/`spec`
 * alanlari sol panel METNINI TASIMIYOR (yalniz sag panel buton/alan
 * etiketlerini tasir — giris akisinin 01 karesinde de AYNI durum: "İşinizin
 * tamamı, tek yerde." ipucu'da YOK ama zaten uygulanmis authWelcomeContent'te
 * VAR). Bu yuzden — TIPKI mevcut authWelcomeContent'in yapildigi gibi —
 * metin doğrudan onayli PNG'den (auth__14/15/16-*.png, ucu de AYNI sol
 * paneli tasiyor) BIREBIR okunur; UYDURULMAZ.
 *
 * BLOK SIRASI: PNG'nin GERCEK gorsel sirasi (headline -> image -> quote,
 * modules/checklist YOK) esas alinir — spec metnindeki "image · headline ·
 * modules · checklist · quote" TUM akislar icin PAYLASILAN genel blok
 * TIPI listesidir, sabit bir SIRA iddiasi degildir (giris akisinin kendi
 * sirasi da: image, SONRA headline — davet'inki TERS).
 *
 * KIRACI ADI DINAMIK (BILEREK, main'e bildirildi): PNG'de "Nordwind
 * Lojistik" ORNEK veridir — TIPKI giris akisinin her yerinde "Acme
 * Teknoloji" gibi (bkz. auth.done.message: "{tenant} çalışma alanına
 * yönlendiriliyorsunuz." — AYNI desen). Sabit "Nordwind Lojistik" YAZILSAYDI
 * HER gercek davette YANLIS sirket adi gosterilirdi — bu, mevcut sorundan
 * (icerik hic yok) daha kotu bir hata olurdu. Baslik bu yuzden {tenant}
 * parametresiyle kurulur; deger App.vue'da peek'ten gelen
 * flow.state.invitationTenantName'den GECER (bkz. App.vue "AKIS" basligi).
 * Kicker/altbaslik/gorsel/alinti/kanit STATIK kalir (PNG'de kiracidan
 * BAGIMSIZ metinler).
 */
export const authInviteWelcomeContent: WelcomeContent<MessageKey> = {
  blocks: [
    {
      type: 'headline',
      kickerKey: 'welcome.invite.kicker',
      titleKey: 'welcome.invite.title',
      subKey: 'welcome.invite.sub',
    },
    { type: 'image', altKey: 'welcome.invite.imageAlt', specKey: 'welcome.invite.imageSpec' },
    {
      type: 'quote',
      textKey: 'welcome.invite.quote.text',
      // NOT: isim (kisi adi) mevcut desende i18n anahtari DEGIL, dogrudan
      // literal (bkz. WelcomeQuoteBlock arayuzu) — bir kisinin adi
      // "cevrilmez". Onayli PNG'den BIREBIR.
      name: 'Petra Voss',
      roleKey: 'welcome.invite.quote.role',
    },
  ],
  // Davet karti PNG'de yalniz IKI kanit satiri gosterir (ulke + uyumluluk) —
  // giris akisinin dorde karsi. Ayni i18n anahtarlari YENIDEN KULLANILIR
  // (metin zaten dogru), yalniz alt kume secilir.
  proofKeys: ['welcome.proof.countries', 'welcome.proof.compliance'],
}

/**
 * Kayit akisi (11-kayit-kimlik / 12-kayit-otp / 13-kayit-parola, FW-129) icin
 * seed. METIN KAYNAGI: onayli PNG'lerin ucu de (auth__11/12/13) AYNI sol
 * paneli tasiyor — metin dogrudan oradan BIREBIR okundu (uydurulmadi).
 * BLOK SIRASI PNG'nin GORSEL sirasidir: gorsel -> baslik -> alinti (giris
 * akisiyla AYNI: gorsel sonra baslik; modules/checklist YOK, davet gibi bir
 * alinti VAR — ikisinin ARASI bir karisim).
 */
export const authRegisterWelcomeContent: WelcomeContent<MessageKey> = {
  blocks: [
    { type: 'image', altKey: 'welcome.register.imageAlt', specKey: 'welcome.register.imageSpec' },
    {
      type: 'headline',
      kickerKey: 'welcome.register.kicker',
      titleKey: 'welcome.register.title',
      subKey: 'welcome.register.sub',
    },
    {
      type: 'quote',
      textKey: 'welcome.register.quote.text',
      // NOT: welcomeContent.ts'in AYNI notunun tekrari — isim (kisi adi)
      // i18n anahtari DEGIL, PNG'den BIREBIR literal (bkz. authInviteWelcomeContent).
      name: 'Petra Voss',
      roleKey: 'welcome.register.quote.role',
    },
  ],
  // Kayit karti PNG'de DORT kanit satiri gosterir (giris akisiyla AYNI dort
  // satir, ayni sira).
  proofKeys: [
    'welcome.proof.countries',
    'welcome.proof.currencies',
    'welcome.proof.compliance',
    'welcome.proof.dataResidency',
  ],
}
